/**
 * Shared "Ingest in Patient Graph" menu action for the medical PA three-dots menus.
 *
 * @module useIngestInPatientGraph
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useRef, useState } from "react";
import { PatientGraphService } from "../../../../api/services/patientGraph/patientGraphService";
import { INGEST_IN_PATIENT_GRAPH_STATUS } from "../../../../api/services/patientGraph/types";
import { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { ToastMessages } from "../../../toasts/toastMessages";

/** Return value of {@link useIngestInPatientGraph}. */
export interface UseIngestInPatientGraphReturn {
  /** Posts the order to ingest; no-ops while a request is already in flight. */
  ingest: () => Promise<void>;
  /** True from the first click until the ingest request settles. */
  loading: boolean;
}

/**
 * Builds the ingest handler for the current order.
 *
 * The backend answers HTTP 200 for skipped and failed ingests as well, so the
 * response `status` decides the toast; a resolved promise alone does not mean
 * the order reached the graph. A second click while `loading` is true is ignored.
 *
 * @param generalOrderDetails - Order snapshot supplying order id and patient MRN
 * @param orgId - Organization id sent with the ingest request
 * @param onComplete - Called after the ingest request settles (success, skip, or fail)
 * @returns Ingest callback and in-flight flag
 */
export const useIngestInPatientGraph = (
  generalOrderDetails: GeneralOrderDetails,
  orgId: string,
  onComplete?: () => void,
): UseIngestInPatientGraphReturn => {
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(false);

  const ingest = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    try {
      const response = await PatientGraphService.ingestPaOrder({
        order_id: generalOrderDetails?.order_id ?? "",
        org_id: orgId,
        patient_mrn: generalOrderDetails?.demographics?.patient_id ?? "",
      });
      if (response?.status === INGEST_IN_PATIENT_GRAPH_STATUS.INGESTED) {
        toast.success(ToastMessages.PATIENT_GRAPH_INGEST_SUCCESS);
        return;
      }
      toast.error(
        response?.status === INGEST_IN_PATIENT_GRAPH_STATUS.SKIPPED
          ? ToastMessages.PATIENT_GRAPH_INGEST_SKIPPED
          : ToastMessages.PATIENT_GRAPH_INGEST_FAILURE,
      );
    } catch {
      toast.error(ToastMessages.PATIENT_GRAPH_INGEST_FAILURE);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      onComplete?.();
    }
  }, [generalOrderDetails, orgId, onComplete]);

  return { ingest, loading };
};
